import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Briefcase,
  Building2,
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Hash,
  Info,
  List,
  Paperclip,
  Plus,
  StickyNote,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass, textareaClass } from "../components/FormField";
import { companyOptions, invoiceHospitalFilterOptions } from "../data";
import { getStoredAccountType } from "../auth";
import { ForfaitQuantityFields } from "../components/ForfaitQuantityFields";
import {
  CurrencyAmountInput,
  formatInvoiceAmount,
  InvoiceCurrencyFields,
  isValidExchangeRateCdf,
  type InvoiceCurrencyCode,
} from "../components/InvoiceCurrencyFields";
import { useHospitalInvoiceSubmit } from "../hooks/useHospitalInvoiceSubmit";
import { AdminSubmitInvoiceWizard } from "./AdminSubmitInvoiceWizard";
import { cn } from "@/shared/lib/cn";

type Step = 0 | 1 | 2;

type InvoiceFormState = {
  provider: string;
  company: string;
  periodFrom: string;
  periodTo: string;
  providerReference: string;
  file: File | null;
  invoiceCurrency: InvoiceCurrencyCode;
  exchangeRateCdf: string;
  totalAmount: string;
  rsspAmount: string;
  notes: string;
};

const initialFormState: InvoiceFormState = {
  provider: "",
  company: "",
  periodFrom: "",
  periodTo: "",
  providerReference: "",
  file: null,
  invoiceCurrency: "USD",
  exchangeRateCdf: "",
  totalAmount: "",
  rsspAmount: "",
  notes: "",
};

function formatDisplayDate(value: string) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function computeAmountToPay(totalAmount: string, rsspAmount: string) {
  const total = Number(totalAmount) || 0;
  const rssp = Number(rsspAmount) || 0;
  return Math.max(0, total - rssp);
}

function SummaryRow({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-border py-3 last:border-0 sm:grid-cols-[200px_1fr] sm:gap-6">
      <span className="text-sm text-muted">{label}</span>
      <span
        className={cn(
          "text-sm text-foreground",
          highlight ? "font-semibold text-green" : "font-medium",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function HospitalSubmitInvoiceWizard() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.submit;
  const [step, setStep] = useState<Step>(0);
  const [fileLabel, setFileLabel] = useState(labels.fields.noFileSelected);
  const {
    form,
    updateForm,
    hospitalName,
    companies,
    conventions,
    selectedCompany,
    selectedConvention,
    isForfait,
    loadingMeta,
    loadingConventions,
    submitting,
    error,
    submittedInvoiceId,
    usingApi,
    submit,
  } = useHospitalInvoiceSubmit();

  const steps = [
    { key: "provider" as const, icon: Building2 },
    { key: "amounts" as const, icon: DollarSign },
    { key: "summary" as const, icon: Check },
  ];

  const canProceedStep0 =
    !!form.companyId &&
    !!form.periodFrom &&
    !!form.periodTo &&
    !!form.conventionId &&
    !!form.file;

  const currencyOk =
    form.invoiceCurrency !== "CDF" || isValidExchangeRateCdf(form.exchangeRateCdf);
  const canProceedStep1 =
    currencyOk &&
    (isForfait
      ? form.forfaitNbFamilles.trim().length > 0 && form.forfaitNbFamillesClient.trim().length > 0
      : form.totalAmount.trim().length > 0);

  const handleNext = () => {
    if (step === 0 && !canProceedStep0) return;
    if (step === 1 && !canProceedStep1) return;
    setStep((current) => Math.min(2, current + 1) as Step);
  };

  const handlePrevious = () => {
    setStep((current) => Math.max(0, current - 1) as Step);
  };

  const handleSubmit = async () => {
    await submit();
  };

  const amountToPay = computeAmountToPay(form.totalAmount, form.rsspAmount);
  const currencyFieldLabels = {
    currency: labels.fields.currency,
    currencyHint: labels.fields.currencyHint,
    exchangeRate: labels.fields.exchangeRate,
    exchangeRateHint: labels.fields.exchangeRateHint,
    currencyUsd: labels.fields.currencyUsd,
    currencyCdf: labels.fields.currencyCdf,
  };
  const conventionLabel = selectedConvention
    ? `${selectedConvention.code ?? selectedConvention.convention_code ?? ""} ${selectedConvention.nom ?? selectedConvention.convention_name ?? ""}`.trim() ||
      `Convention #${selectedConvention.id}`
    : labels.summary.noConvention;

  if (submittedInvoiceId) {
    return (
      <>
        <Breadcrumbs
          homeHref="/dashboard/hospital"
          items={[
            { label: t.safecheck.invoices.billing },
            { label: labels.title },
          ]}
        />
        <div className="theme-card p-6">
          <div className="rounded-xl border border-green/30 bg-green/10 px-4 py-3 text-sm text-foreground">
            {labels.success} (#{submittedInvoiceId})
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to={`/dashboard/invoices/${submittedInvoiceId}`}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              Voir la facture
            </Link>
            <Link
              to="/dashboard/invoices"
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-card-muted"
            >
              {labels.viewPending}
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/hospital"
        items={[
          { label: t.safecheck.invoices.billing },
          { label: labels.title },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-6 py-5">
          <h1 className="text-lg font-semibold text-foreground">{labels.cardTitle}</h1>
          {usingApi && (
            <p className="mt-1 text-xs text-muted">
              Prestataire : {hospitalName}
              {loadingMeta ? " — chargement…" : ""}
            </p>
          )}
          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </div>

        <div className="border-b border-border px-6 py-6">
          <div className="mx-auto flex max-w-3xl items-center justify-between">
            {steps.map((item, index) => {
              const Icon = item.icon;
              const isActive = step === index;
              const isCompleted = step > index;

              return (
                <div key={item.key} className="flex flex-1 items-center">
                  <div className="flex flex-col items-center gap-2">
                    <div
                      className={cn(
                        "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors",
                        isCompleted && "border-green bg-green text-white",
                        isActive && "border-primary bg-primary text-white",
                        !isActive && !isCompleted && "border-border bg-card text-muted",
                      )}
                    >
                      <Icon size={18} />
                    </div>
                    <span
                      className={cn(
                        "max-w-[120px] text-center text-xs font-medium",
                        isCompleted && "text-green",
                        isActive && "text-primary",
                        !isActive && !isCompleted && "text-muted",
                      )}
                    >
                      {labels.steps[item.key]}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "mx-3 mb-6 h-px flex-1",
                        step > index ? "bg-green" : "bg-border",
                      )}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-6 p-6">
          {step === 0 && (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField label={labels.fields.provider} required>
                  <input value={hospitalName} disabled className={`${inputClass} opacity-70`} />
                </FormField>

                <FormField label={labels.fields.company} required>
                  <div className="relative">
                    <Briefcase
                      size={14}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                    />
                    <select
                      value={form.companyId}
                      onChange={(event) => {
                        updateForm("companyId", event.target.value);
                        updateForm("conventionId", "");
                      }}
                      className={`${selectClass} pl-9`}
                    >
                      <option value="">{labels.fields.companyPlaceholder}</option>
                      {companies.map((company) => (
                        <option key={company.id} value={String(company.id)}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </FormField>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField label={labels.fields.periodFrom} required>
                  <input
                    type="date"
                    value={form.periodFrom}
                    onChange={(event) => updateForm("periodFrom", event.target.value)}
                    className={inputClass}
                  />
                </FormField>
                <FormField label={labels.fields.periodTo} required>
                  <input
                    type="date"
                    value={form.periodTo}
                    onChange={(event) => updateForm("periodTo", event.target.value)}
                    className={inputClass}
                  />
                </FormField>
                <FormField label={labels.fields.providerReference}>
                  <input
                    value={form.providerReference}
                    onChange={(event) => updateForm("providerReference", event.target.value)}
                    placeholder={labels.fields.providerReferencePlaceholder}
                    className={inputClass}
                  />
                </FormField>
              </div>

              <FormField label="Convention" required>
                <select
                  value={form.conventionId}
                  onChange={(event) => updateForm("conventionId", event.target.value)}
                  className={selectClass}
                  disabled={loadingConventions || !form.companyId}
                >
                  <option value="">
                    {!form.companyId
                      ? "Choisir d'abord une entreprise"
                      : loadingConventions
                        ? "Chargement…"
                        : conventions.length === 0
                          ? "Aucune convention éligible"
                          : "Sélectionner une convention"}
                  </option>
                  {conventions.map((convention) => {
                    const code = convention.code || convention.convention_code || "";
                    const nom = convention.nom || convention.convention_name || `Convention #${convention.id}`;
                    return (
                      <option key={convention.id} value={String(convention.id)}>
                        {code ? `${code} — ` : ""}
                        {nom} ({convention.mode_traitement_label || convention.type_traitement || "—"})
                      </option>
                    );
                  })}
                </select>
              </FormField>

              <FormField label="Nature de facture" required>
                <select
                  value={form.nature}
                  onChange={(event) =>
                    updateForm(
                      "nature",
                      event.target.value as "forfait_mensuel" | "hors_forfait",
                    )
                  }
                  className={selectClass}
                >
                  <option value="forfait_mensuel">Forfait mensuel</option>
                  <option value="hors_forfait">Hors forfait</option>
                </select>
              </FormField>

              <FormField label={labels.fields.attachment} required>
                <label className="flex h-10 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 text-sm text-muted-foreground transition-colors hover:bg-card-muted">
                  <Paperclip size={14} className="shrink-0 text-muted" />
                  <span className="rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-foreground">
                    {labels.fields.chooseFile}
                  </span>
                  <span className="truncate">{fileLabel}</span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0] ?? null;
                      updateForm("file", file);
                      setFileLabel(file ? file.name : labels.fields.noFileSelected);
                    }}
                  />
                </label>
                <p className="mt-1.5 text-xs text-muted">{labels.fields.attachmentHint}</p>
              </FormField>
            </>
          )}

          {step === 1 && (
            <>
              <InvoiceCurrencyFields
                currency={form.invoiceCurrency}
                exchangeRateCdf={form.exchangeRateCdf}
                onCurrencyChange={(value) => {
                  updateForm("invoiceCurrency", value);
                  if (value === "USD") updateForm("exchangeRateCdf", "");
                }}
                onExchangeRateChange={(value) => updateForm("exchangeRateCdf", value)}
                labels={currencyFieldLabels}
              />
              {isForfait ? (
                <ForfaitQuantityFields
                  preview={selectedConvention?.forfait_preview}
                  nbPrestataire={form.forfaitNbFamilles}
                  nbClient={form.forfaitNbFamillesClient}
                  onNbPrestataireChange={(value) => updateForm("forfaitNbFamilles", value)}
                  onNbClientChange={(value) => updateForm("forfaitNbFamillesClient", value)}
                />
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <DollarSign size={14} className="text-primary" />
                      {labels.fields.totalAmount}
                      <span className="text-pink">*</span>
                    </label>
                    <CurrencyAmountInput
                      value={form.totalAmount}
                      onChange={(value) => updateForm("totalAmount", value)}
                      currency={form.invoiceCurrency}
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Plus size={14} className="text-yellow" />
                      {labels.fields.rsspAmount}
                    </label>
                    <CurrencyAmountInput
                      value={form.rsspAmount}
                      onChange={(value) => updateForm("rsspAmount", value)}
                      currency={form.invoiceCurrency}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <StickyNote size={14} className="text-muted" />
                  {labels.fields.notes}
                </label>
                <textarea
                  value={form.notes}
                  onChange={(event) => updateForm("notes", event.target.value)}
                  placeholder={labels.fields.notesPlaceholder}
                  className={textareaClass}
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-2 flex items-center gap-2">
                <List size={16} className="text-primary" />
                <h2 className="text-sm font-semibold text-foreground">{labels.summaryTitle}</h2>
              </div>
              <div className="rounded-xl border border-border bg-card px-5 py-1">
                <SummaryRow label={labels.summary.provider} value={hospitalName} />
                <SummaryRow
                  label={labels.summary.company}
                  value={selectedCompany?.name ?? "—"}
                />
                <SummaryRow label={labels.summary.convention} value={conventionLabel} />
                <SummaryRow
                  label={labels.summary.treatmentMode}
                  value={selectedConvention?.type_traitement ?? "—"}
                />
                <SummaryRow
                  label={labels.summary.invoiceNature}
                  value={
                    isForfait ? "Forfait mensuel" : labels.summary.outOfPackage
                  }
                />
                <SummaryRow
                  label={labels.summary.period}
                  value={`${formatDisplayDate(form.periodFrom)} → ${formatDisplayDate(form.periodTo)}`}
                />
                <SummaryRow
                  label={labels.summary.reference}
                  value={form.providerReference || "—"}
                />
                <SummaryRow label={labels.summary.currency} value={form.invoiceCurrency} />
                {form.invoiceCurrency === "CDF" && (
                  <SummaryRow
                    label={labels.summary.exchangeRate}
                    value={`${form.exchangeRateCdf || "—"} FC/USD`}
                  />
                )}
                {!isForfait && (
                  <>
                    <SummaryRow
                      label={labels.summary.invoiceAmount}
                      value={formatInvoiceAmount(form.totalAmount, form.invoiceCurrency)}
                    />
                    <SummaryRow
                      label={labels.summary.amountToPay}
                      value={formatInvoiceAmount(amountToPay, form.invoiceCurrency)}
                      highlight
                    />
                  </>
                )}
                <SummaryRow label={labels.summary.file} value={form.file?.name ?? "—"} />
                {form.notes && <SummaryRow label={labels.summary.notes} value={form.notes} />}
              </div>
            </>
          )}
        </div>

        <div
          className={cn(
            "flex items-center gap-3 border-t border-border px-6 py-4",
            step > 0 ? "justify-between" : "justify-end",
          )}
        >
          {step > 0 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <ChevronLeft size={16} />
              {labels.previous}
            </button>
          )}
          {step < 2 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={step === 0 ? !canProceedStep0 : !canProceedStep1}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {labels.next}
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-green px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green/90 disabled:opacity-50"
            >
              <Check size={16} />
              {submitting ? "Soumission…" : labels.submit}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function DemoSubmitInvoiceWizard() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.submit;
  const [step, setStep] = useState<Step>(0);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<InvoiceFormState>(initialFormState);
  const [fileLabel, setFileLabel] = useState(labels.fields.noFileSelected);

  const providerOptions = useMemo(
    () => invoiceHospitalFilterOptions.filter((option) => option.value),
    [],
  );
  const enterpriseOptions = useMemo(
    () => companyOptions.filter((option) => option.value),
    [],
  );

  const steps = [
    { key: "provider" as const, icon: Building2 },
    { key: "amounts" as const, icon: DollarSign },
    { key: "summary" as const, icon: Check },
  ];

  const updateForm = <K extends keyof InvoiceFormState>(key: K, value: InvoiceFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const canProceedStep0 =
    form.provider && form.company && form.periodFrom && form.periodTo && form.file;
  const currencyOk =
    form.invoiceCurrency !== "CDF" || isValidExchangeRateCdf(form.exchangeRateCdf);
  const canProceedStep1 = currencyOk && form.totalAmount.trim().length > 0;

  const handleNext = () => {
    if (step === 0 && !canProceedStep0) return;
    if (step === 1 && !canProceedStep1) return;
    setStep((current) => Math.min(2, current + 1) as Step);
  };

  const handlePrevious = () => {
    setStep((current) => Math.max(0, current - 1) as Step);
  };

  const providerLabel =
    providerOptions.find((option) => option.value === form.provider)?.label ?? "—";
  const companyLabel =
    enterpriseOptions.find((option) => option.value === form.company)?.label ?? "—";
  const amountToPay = computeAmountToPay(form.totalAmount, form.rsspAmount);
  const currencyFieldLabels = {
    currency: labels.fields.currency,
    currencyHint: labels.fields.currencyHint,
    exchangeRate: labels.fields.exchangeRate,
    exchangeRateHint: labels.fields.exchangeRateHint,
    currencyUsd: labels.fields.currencyUsd,
    currencyCdf: labels.fields.currencyCdf,
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.invoices.billing },
          { label: labels.title },
        ]}
      />

      <div className="theme-card">
        <div className="border-b border-border px-6 py-5">
          <h1 className="text-lg font-semibold text-foreground">{labels.cardTitle}</h1>
        </div>

        {submitted ? (
          <div className="p-6">
            <div className="rounded-xl border border-green/30 bg-green/10 px-4 py-3 text-sm text-foreground">
              {labels.success}
            </div>
            <div className="mt-4">
              <Link
                to="/dashboard/invoices/pending"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
              >
                {labels.viewPending}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="border-b border-border px-6 py-6">
              <div className="mx-auto flex max-w-3xl items-center justify-between">
                {steps.map((item, index) => {
                  const Icon = item.icon;
                  const isActive = step === index;
                  const isCompleted = step > index;

                  return (
                    <div key={item.key} className="flex flex-1 items-center">
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className={cn(
                            "flex h-11 w-11 items-center justify-center rounded-full border-2 transition-colors",
                            isCompleted && "border-green bg-green text-white",
                            isActive && "border-primary bg-primary text-white",
                            !isActive && !isCompleted && "border-border bg-card text-muted",
                          )}
                        >
                          <Icon size={18} />
                        </div>
                        <span
                          className={cn(
                            "max-w-[120px] text-center text-xs font-medium",
                            isCompleted && "text-green",
                            isActive && "text-primary",
                            !isActive && !isCompleted && "text-muted",
                          )}
                        >
                          {labels.steps[item.key]}
                        </span>
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={cn(
                            "mx-3 mb-6 h-px flex-1",
                            step > index ? "bg-green" : "bg-border",
                          )}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="space-y-6 p-6">
              {step === 0 && (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormField label={labels.fields.provider} required>
                      <select
                        value={form.provider}
                        onChange={(event) => updateForm("provider", event.target.value)}
                        className={selectClass}
                      >
                        <option value="">{labels.fields.providerPlaceholder}</option>
                        {providerOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label={labels.fields.company} required>
                      <select
                        value={form.company}
                        onChange={(event) => updateForm("company", event.target.value)}
                        className={selectClass}
                      >
                        <option value="">{labels.fields.companyPlaceholder}</option>
                        {enterpriseOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <FormField label={labels.fields.periodFrom} required>
                      <input
                        type="date"
                        value={form.periodFrom}
                        onChange={(event) => updateForm("periodFrom", event.target.value)}
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label={labels.fields.periodTo} required>
                      <input
                        type="date"
                        value={form.periodTo}
                        onChange={(event) => updateForm("periodTo", event.target.value)}
                        className={inputClass}
                      />
                    </FormField>
                    <FormField label={labels.fields.providerReference}>
                      <input
                        value={form.providerReference}
                        onChange={(event) => updateForm("providerReference", event.target.value)}
                        className={inputClass}
                      />
                    </FormField>
                  </div>
                  <FormField label={labels.fields.attachment} required>
                    <label className="flex h-10 cursor-pointer items-center gap-3 rounded-lg border border-border px-3 text-sm text-muted-foreground hover:bg-card-muted">
                      <Paperclip size={14} />
                      <span className="truncate">{fileLabel}</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0] ?? null;
                          updateForm("file", file);
                          setFileLabel(file ? file.name : labels.fields.noFileSelected);
                        }}
                      />
                    </label>
                  </FormField>
                </>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <InvoiceCurrencyFields
                    currency={form.invoiceCurrency}
                    exchangeRateCdf={form.exchangeRateCdf}
                    onCurrencyChange={(value) => {
                      updateForm("invoiceCurrency", value);
                      if (value === "USD") updateForm("exchangeRateCdf", "");
                    }}
                    onExchangeRateChange={(value) => updateForm("exchangeRateCdf", value)}
                    labels={currencyFieldLabels}
                  />
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <FormField label={labels.fields.totalAmount} required>
                      <CurrencyAmountInput
                        value={form.totalAmount}
                        onChange={(value) => updateForm("totalAmount", value)}
                        currency={form.invoiceCurrency}
                      />
                    </FormField>
                    <FormField label={labels.fields.rsspAmount}>
                      <CurrencyAmountInput
                        value={form.rsspAmount}
                        onChange={(value) => updateForm("rsspAmount", value)}
                        currency={form.invoiceCurrency}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="rounded-xl border border-border bg-card px-5 py-1">
                  <SummaryRow label={labels.summary.provider} value={providerLabel} />
                  <SummaryRow label={labels.summary.company} value={companyLabel} />
                  <SummaryRow
                    label={labels.summary.period}
                    value={`${formatDisplayDate(form.periodFrom)} → ${formatDisplayDate(form.periodTo)}`}
                  />
                  <SummaryRow label={labels.summary.currency} value={form.invoiceCurrency} />
                  {form.invoiceCurrency === "CDF" && (
                    <SummaryRow
                      label={labels.summary.exchangeRate}
                      value={`${form.exchangeRateCdf || "—"} FC/USD`}
                    />
                  )}
                  <SummaryRow
                    label={labels.summary.amountToPay}
                    value={formatInvoiceAmount(amountToPay, form.invoiceCurrency)}
                    highlight
                  />
                </div>
              )}
            </div>

            <div
              className={cn(
                "flex items-center gap-3 border-t border-border px-6 py-4",
                step > 0 ? "justify-between" : "justify-end",
              )}
            >
              {step > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm"
                >
                  <ChevronLeft size={16} />
                  {labels.previous}
                </button>
              )}
              {step < 2 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={step === 0 ? !canProceedStep0 : !canProceedStep1}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  {labels.next}
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setSubmitted(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-green px-4 py-2 text-sm font-medium text-white"
                >
                  <Check size={16} />
                  {labels.submit}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export function SubmitInvoicePage() {
  const [accountType] = useState(() => getStoredAccountType());

  if (accountType === "hospital") {
    return <HospitalSubmitInvoiceWizard />;
  }

  if (accountType === "admin") {
    return <AdminSubmitInvoiceWizard />;
  }

  return <DemoSubmitInvoiceWizard />;
}
